import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { MessageSquare, Mail, Phone, Send, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Communications = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [sending, setSending] = useState(false);
  
  const [smsForm, setSmsForm] = useState({ phone: '', message: '' });
  const [emailForm, setEmailForm] = useState({ email: '', subject: '', content: '' });

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [messagesRes, statsRes, configRes] = await Promise.all([
        axios.get(`${API}/communications/messages/history?limit=30`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/communications/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/communications/config`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setMessages(messagesRes.data);
      setStats(statsRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load communication data');
    } finally {
      setLoading(false);
    }
  };

  const sendSMS = async (e) => {
    e.preventDefault();
    if (!smsForm.phone || !smsForm.message) return;
    
    try {
      setSending(true);
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${API}/communications/sms/send`, {
        to_phone: smsForm.phone,
        message: smsForm.message,
        message_type: 'notification'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('SMS sent successfully!');
      setSmsForm({ phone: '', message: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.email || !emailForm.subject || !emailForm.content) return;
    
    try {
      setSending(true);
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${API}/communications/email/send`, {
        to_email: emailForm.email,
        subject: emailForm.subject,
        content: emailForm.content,
        content_type: 'html'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Email sent successfully!');
      setEmailForm({ email: '', subject: '', content: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Communications</h1>
          <p className="text-slate-600 mt-1">Send SMS and Email notifications</p>
        </div>
      </div>

      {config && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">Status:</span>
            {config.twilio_configured ? (
              <Badge className="bg-emerald-500">Twilio Active</Badge>
            ) : (
              <Badge className="bg-amber-500">Twilio Mock Mode</Badge>
            )}
            {config.sendgrid_configured ? (
              <Badge className="bg-emerald-500">SendGrid Active</Badge>
            ) : (
              <Badge className="bg-amber-500">SendGrid Mock Mode</Badge>
            )}
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <Phone size={18} />
                SMS Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.total_sms_sent}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <Mail size={18} />
                Emails Sent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{stats.total_emails_sent}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">SMS Delivery Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{stats.sms_delivery_rate}%</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-cyan-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Email Delivery Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-cyan-600">{stats.email_delivery_rate}%</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone size={20} />
              Send SMS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={sendSMS} className="space-y-4">
              <div>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="+1234567890"
                  value={smsForm.phone}
                  onChange={(e) => setSmsForm({...smsForm, phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  placeholder="Your message here..."
                  rows={4}
                  value={smsForm.message}
                  onChange={(e) => setSmsForm({...smsForm, message: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {smsForm.message.length}/160 characters
                </p>
              </div>
              <Button type="submit" disabled={sending} className="w-full gap-2">
                <Send size={16} />
                {sending ? 'Sending...' : 'Send SMS'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail size={20} />
              Send Email
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={sendEmail} className="space-y-4">
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="guest@example.com"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm({...emailForm, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  placeholder="Email subject"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                />
              </div>
              <div>
                <Label>Content</Label>
                <Textarea
                  placeholder="Email content..."
                  rows={4}
                  value={emailForm.content}
                  onChange={(e) => setEmailForm({...emailForm, content: e.target.value})}
                />
              </div>
              <Button type="submit" disabled={sending} className="w-full gap-2">
                <Send size={16} />
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare size={20} />
            Recent Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          {messages.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No messages sent yet
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className={`p-2 rounded-lg ${
                    msg.type === 'sms' ? 'bg-blue-100' : 'bg-purple-100'
                  }`}>
                    {msg.type === 'sms' ? <Phone size={18} /> : <Mail size={18} />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900">{msg.recipient}</span>
                      {msg.status === 'sent' || msg.status === 'delivered' ? (
                        <CheckCircle2 className="text-emerald-500" size={16} />
                      ) : (
                        <XCircle className="text-red-500" size={16} />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2">{msg.message}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {format(new Date(msg.sent_at), 'PPp')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Communications;
